import { isEmpty, orderBy } from 'lodash';
import { string } from 'prop-types';
import { useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { MultiActionButton, SanitizedHtml, Skeleton, StrapiImg, useHomePageContext } from 'components';
import { getModalContent } from 'helpers';
import { useFetch } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

const CseBenefitsList = ({ title }) => {
  const navigate = useNavigate();
  const { pageType } = useHomePageContext();

  const {
    call: cseBenefitsCall,
    loading,
    data: cseBenefitsData,
    setData: setCseBenefitsData
  } = useFetch({ filterUserCompany: true, withSetState: false });

  const cseBenefits = cseBenefitsData?.data;

  useEffect(() => {
    getCSEBenefits();
  }, [pageType]);

  const getCSEBenefits = async () => {
    const { data: benefitsResponse } =
      (await cseBenefitsCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: '/api/cse-benefits',
        query: {
          filters: {
            page_types: { name: { $eq: pageType } }
          }
        }
      })) || {};

    const benefits = benefitsResponse?.body?.data;

    setCseBenefitsData({
      ...benefitsResponse,
      data: orderBy(benefits, ['order'])
    });
  };

  const goToBenefitShow = (cseBenefit) => {
    const benefitPath = generatePath(routes.cseBenefitShow.path, { id: cseBenefit.id });
    navigate(benefitPath);
  };

  return (
    <>
      {loading && (
        <div className="grid gap-8 lg:grid-cols-2">
          {[...Array(6)].map((_, index) => (
            <div className="grid grid-cols-12 gap-4 lg:gap-8" key={`Skeleton-${index}`}>
              <div className="col-span-3 aspect-square lg:col-span-4 2xl:col-span-2">
                <Skeleton type="text" classNames="h-full w-full" />
              </div>
              <div className="col-span-9 flex flex-col justify-between pr-4 lg:col-span-8 2xl:col-span-10">
                <Skeleton type="text" classNames="w-1/2 md:h-8" />
                <Skeleton type="text" classNames="w-full md:h-8" />
                <Skeleton type="text" classNames="w-1/3 md:h-8" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <div className="cse-benefits mb-20">
          <h2 className="h2 underlined-yellow mb-12">{title}</h2>

          <div className="grid auto-rows-max gap-8 lg:grid-cols-2">
            {cseBenefits?.map((cseBenefit) => (
              <div className="flex gap-4" key={`cse-benefits-${cseBenefit.id}`}>
                <div className="aspect-square h-20 rounded-2xl overflow-hidden shrink-0 md:h-44">
                  <StrapiImg image={cseBenefit.thumbnail} className="object-cover w-full h-full" />
                </div>

                <div className="flex flex-col justify-between gap-2">
                  <SanitizedHtml content={cseBenefit.excerpt} className="truncate-line three-lines" />

                  {/* Redirect to CSE Benefit show page */}
                  {!isEmpty(cseBenefit.buttonText) && (
                    <button
                      type="button"
                      onClick={() => goToBenefitShow(cseBenefit)}
                      className="btn small w-fit text-sm lg:text-base"
                    >
                      {cseBenefit.buttonText}
                    </button>
                  )}

                  {/* Redirect to external page outside of the app */}
                  {!isEmpty(cseBenefit.cta[0]) && (
                    <MultiActionButton
                      cta={cseBenefit.cta[0]}
                      modalContent={(i, type) => getModalContent(cseBenefit, i, type)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

CseBenefitsList.propTypes = {
  title: string
};

export default CseBenefitsList;
